// hooks/useCompanyDropdown.js
// Infinite-scroll + debounced search hook for company dropdown
import { useState, useEffect, useCallback, useRef } from 'react';
import { companySettingsController } from '../controllers/companySettingsController';

const LIMIT = 15;

export function useCompanyDropdown() {
  const [companies,   setCompanies]   = useState([]);
  const [search,      setSearch]      = useState('');
  const [page,        setPage]        = useState(1);
  const [hasMore,     setHasMore]     = useState(true);
  const [loading,     setLoading]     = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error,       setError]       = useState(null);

  const debounceRef = useRef(null);
  const searchRef   = useRef(search);
  searchRef.current = search;

  // ── core fetch ────────────────────────────────────────
  const fetchPage = useCallback(async (pageNum, searchVal, replace = false) => {
    if (pageNum === 1) setLoading(true);
    else               setLoadingMore(true);

    setError(null);
    try {
      const res = await companySettingsController.getAllCompanies({
        page:   pageNum,
        limit:  LIMIT,
        search: searchVal,
        sortBy: 'companyName',
        order:  'asc',
      });

      if (res.success) {
        const mapped = (res.data || []).map(c => ({
          id:    c._id  || c.id,
          name:  c.companyName,
          email: c.email,
          logo:  c.logo,
          color: c.primaryColor || '#3b82f6',
        }));

        setCompanies(prev => replace ? mapped : [...prev, ...mapped]);
        const { totalPages = 1 } = res.pagination || {};
        setHasMore(pageNum < totalPages);
      } else {
        setError(res.error || res.message || 'Failed to load companies');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // ── initial + search-change load ──────────────────────
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      fetchPage(1, search, true);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [search, fetchPage]);

  // ── load next page ────────────────────────────────────
  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    const next = page + 1;
    setPage(next);
    fetchPage(next, searchRef.current, false);
  }, [loadingMore, hasMore, page, fetchPage]);

  const reset = useCallback(() => {
    setSearch('');
    setPage(1);
    setCompanies([]);
    setHasMore(true);
  }, []);

  return {
    companies,
    search,
    setSearch,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    error,
    reset,
  };
}